import { Component } from '@theme/component';

const storageKey = 'hexo-accessibility-v1';
const keys = ['large_text', 'links', 'motion'];
const attributes = { large_text: 'data-hx-accessibility-large-text', links: 'data-hx-accessibility-links', motion: 'data-hx-reduce-motion' };
const read = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(storageKey) || '{}');
    return Object.fromEntries(keys.map(key => [key, stored?.[key] === true]));
  } catch { return {}; }
};

// One page-level owner for automatic video motion. This also covers videos
// inserted by the theme editor and components that attempt playback later.
const automaticVideoSelector = 'video[autoplay], .hx-hero video, hexo-campaign-film video';
const motionPreference = matchMedia('(prefers-reduced-motion: reduce)');
const automaticVideos = new Map();
const motionReduced = () => motionPreference.matches || document.documentElement.hasAttribute(attributes.motion);

const registerAutomaticVideo = video => {
  if (automaticVideos.has(video)) return automaticVideos.get(video);
  const state = { playing: !video.paused && !video.ended, stopped: false, resume: false };
  automaticVideos.set(video, state);
  return state;
};

const syncAutomaticMotion = () => {
  const reduced = motionReduced();
  for (const [video, state] of automaticVideos) {
    if (!video.isConnected) {
      automaticVideos.delete(video);
      continue;
    }
    if (reduced) {
      // Only remember playback that was active before we stopped motion.
      // A film already paused by its own controls is never restarted here.
      if (!state.stopped) state.resume = !video.ended && (!video.paused || state.playing);
      state.stopped = true;
      video.pause();
    } else {
      state.stopped = false;
      if (state.resume && !document.hidden) {
        state.resume = false;
        video.play()?.catch(() => { /* Browser autoplay restrictions remain authoritative. */ });
      }
    }
  }
};

const discoverAutomaticVideos = root => {
  if (root.matches?.(automaticVideoSelector)) registerAutomaticVideo(root);
  root.querySelectorAll?.(automaticVideoSelector).forEach(registerAutomaticVideo);
};

// Media events do not bubble, so use capture. Prevent component observers or
// the native autoplay algorithm from restarting a film while motion is stopped.
document.addEventListener('play', event => {
  const video = event.target;
  if (!(video instanceof HTMLVideoElement) || !video.matches(automaticVideoSelector)) return;
  const state = registerAutomaticVideo(video);
  if (motionReduced()) {
    state.stopped = true;
    video.pause();
  } else state.playing = true;
}, true);
document.addEventListener('pause', event => {
  const state = automaticVideos.get(event.target);
  if (state && !motionReduced()) state.playing = false;
}, true);
for (const type of ['ended', 'emptied']) {
  document.addEventListener(type, event => {
    const state = automaticVideos.get(event.target);
    if (state) { state.playing = false; state.resume = false; }
  }, true);
}

const observeAutomaticVideos = () => {
  discoverAutomaticVideos(document);
  syncAutomaticMotion();
  new MutationObserver(records => {
    for (const record of records) {
      if (record.type === 'attributes') discoverAutomaticVideos(record.target);
      else record.addedNodes.forEach(discoverAutomaticVideos);
    }
    syncAutomaticMotion();
  }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['autoplay'] });
};

motionPreference.addEventListener('change', syncAutomaticMotion);
document.addEventListener('hexo:accessibility-change', syncAutomaticMotion);
document.addEventListener('visibilitychange', syncAutomaticMotion);
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', observeAutomaticVideos, { once: true });
else observeAutomaticVideos();

class HexoAccessibility extends Component {
  #controller;
  #preferences = read();

  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#controller = new AbortController();
    const options = { signal: this.#controller.signal };
    this.addEventListener('change', this.#change, options);
    this.querySelector('[data-hx-accessibility-reset]')?.addEventListener('click', this.#reset, options);
    window.addEventListener('storage', event => {
      if (event.key !== storageKey && event.key !== null) return;
      this.#preferences = read();
      this.#apply();
    }, options);
    this.#apply();
  }

  updatedCallback() { super.updatedCallback(); this.#apply(); }
  disconnectedCallback() { super.disconnectedCallback(); this.#controller?.abort(); }

  #apply() {
    for (const key of keys) {
      const enabled = this.#preferences[key] === true;
      document.documentElement.toggleAttribute(attributes[key], enabled);
      const input = this.querySelector(`[data-hx-accessibility="${key}"]`);
      if (input) input.checked = enabled;
    }
    // Snapshot active playback before existing component listeners pause it.
    syncAutomaticMotion();
    document.dispatchEvent(new Event('hexo:accessibility-change'));
  }

  #save(message) {
    let saved = true;
    try {
      if (keys.some(key => this.#preferences[key])) localStorage.setItem(storageKey, JSON.stringify(this.#preferences));
      else localStorage.removeItem(storageKey);
    } catch { saved = false; }
    this.#apply();
    const status = this.querySelector('[data-hx-accessibility-status]');
    if (status) status.textContent = saved ? message : this.dataset.session;
  }

  #change = event => {
    const key = event.target.dataset.hxAccessibility;
    if (!keys.includes(key)) return;
    this.#preferences[key] = event.target.checked;
    this.#save(this.dataset.updated);
  };

  #reset = () => { this.#preferences = {}; this.#save(this.dataset.reset); };
}

if (!customElements.get('hexo-accessibility')) customElements.define('hexo-accessibility', HexoAccessibility);

